import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserCheck, LayoutGrid, Users, Briefcase, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from '../lib/toast';
import PublicCoachHeader from './PublicCoachHeader';
import { usePublicTheme } from '../hooks/usePublicTheme';
import '../styles/publicTheme.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  { icon: UserCheck, text: 'ملف مهني كامل وموثّق وسيرة ذاتية قابلة للتصدير' },
  { icon: LayoutGrid, text: 'استديو تكتيكي وتمارين وخطط تدريبية غير محدودة' },
  { icon: Users, text: 'إدارة طاقم عمل ومشاركة تمارينك وخططك بصلاحيات' },
  { icon: Briefcase, text: 'ظهور بالدليل العام واستقبال عروض المنشآت' },
];

export default function CoachRegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = usePublicTheme();
  const [form, setForm] = useState({ username: '', password: '', full_name: '', email: '', phone: '' });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inviteToken = new URLSearchParams(location.search).get('invite');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) { setError('يجب الموافقة على الشروط والأحكام وسياسة الخصوصية للاستمرار'); return; }
    setError(null);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/coaches/register`, form);
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user_info));
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
      toast.success('تم إنشاء حسابك بنجاح، أكمل ملفك المهني الآن');
      if (inviteToken) {
        try {
          await axios.post(`${API}/coaches/crew/invites/token/${inviteToken}/redeem`, {}, { headers: { Authorization: `Bearer ${res.data.access_token}` } });
          window.location.href = '/coach/crew';
          return;
        } catch { /* invite may be invalid/expired — continue normally */ }
      }
      window.location.href = '/coach/profile';
    } catch (err) {
      setError(err?.response?.data?.detail || 'فشل إنشاء الحساب، تحقق من البيانات');
    }
    setLoading(false);
  };

  const inputCls = "w-full rounded-xl border border-white/15 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-colors focus:border-teal-500/60 focus:bg-white/[0.05]";

  return (
    <div className="coaches-landing-root min-h-screen bg-[#060f1c] text-white" dir="rtl" data-testid="coach-register-page" data-pub-theme={theme}>
      <PublicCoachHeader title="انضم كمدرب" dark={theme === 'dark'} theme={theme} onToggleTheme={toggleTheme} />

      <div className="relative overflow-hidden bg-[#060f1c] pb-16 pt-28">
        <div className="absolute inset-0 bg-[#060f1c]">
          <div className="absolute -top-24 -end-24 h-96 w-96 rounded-full bg-teal-500/10 blur-3xl" />
          <div className="absolute -bottom-16 -start-16 h-80 w-80 rounded-full bg-orange-500/10 blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto grid max-w-5xl grid-cols-1 gap-10 px-5 lg:grid-cols-2 lg:items-center">
          {/* لوحة المزايا */}
          <div className="hidden lg:block">
            <span className="font-display inline-block rounded-full border border-teal-500/20 bg-teal-500/10 px-4 py-1.5 text-xs font-bold tracking-wide text-teal-400">
              تسيار للمدربين
            </span>
            <h1 className="font-display mt-5 text-3xl font-black leading-tight text-white sm:text-4xl">
              انضم كمدرب في تسيار
            </h1>
            <p className="mt-4 max-w-md text-base leading-relaxed text-slate-300">
              أنشئ حسابك المستقل، وأكمل ملفك المهني بعدها. مساحة عملك الفنية والمهنية جاهزة من أول دقيقة.
            </p>
            <ul className="mt-8 space-y-4">
              {BENEFITS.map((b) => (
                <li key={b.text} className="flex items-start gap-3 text-sm text-slate-300">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 text-teal-400">
                    <b.icon className="h-4 w-4" />
                  </span>
                  <span className="pt-1.5">{b.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* البطاقة/الفورم */}
          <div className="mx-auto w-full max-w-md">
            <div className="mb-6 text-center lg:hidden">
              <h1 className="font-display text-2xl font-bold text-white">انضم كمدرب في تسيار</h1>
              <p className="mt-2 text-sm text-slate-400">أنشئ حسابك المستقل، وأكمل ملفك المهني بعدها</p>
            </div>

            {inviteToken && (
              <div className="mb-4 flex items-center gap-2 rounded-xl border border-teal-500/25 bg-teal-500/10 px-4 py-3 text-sm text-teal-300" data-testid="coach-register-invite-banner">
                <Sparkles className="h-4 w-4 shrink-0" />
                لديك دعوة انضمام إلى طاقم عمل — سيتم ربط حسابك بها تلقائياً بعد إنشاء الحساب
              </div>
            )}

            <form onSubmit={submit} className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)] backdrop-blur-md" data-testid="coach-register-form">
              {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="coach-register-error">{error}</div>}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-300">الاسم الكامل</label>
                <input required className={inputCls} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="coach-register-fullname" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-300">اسم المستخدم</label>
                <input required className={inputCls} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="coach-register-username" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-300">البريد الإلكتروني</label>
                <input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="coach-register-email" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-300">رقم الجوال</label>
                <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="coach-register-phone" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-300">كلمة المرور</label>
                <input required type="password" minLength={6} className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="coach-register-password" />
              </div>

              <label className="flex items-start gap-2.5 pt-1 text-xs leading-relaxed text-slate-400">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  data-testid="coach-register-terms-checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/5 text-teal-500 focus:ring-teal-500/50"
                />
                <span>
                  أوافق على <a href="/terms" target="_blank" rel="noreferrer" className="font-semibold text-teal-400 hover:underline">الشروط والأحكام</a> و<a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold text-teal-400 hover:underline">سياسة الخصوصية</a>
                </span>
              </label>

              <button
                type="submit"
                disabled={loading || !agreedToTerms}
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-l from-orange-500 to-orange-400 py-3.5 text-sm font-bold text-white shadow-[0_0_15px_rgba(245,76,7,0.4)] transition-all hover:shadow-[0_0_22px_rgba(245,76,7,0.6)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:shadow-[0_0_15px_rgba(245,76,7,0.4)]"
                data-testid="coach-register-submit-btn"
              >
                {loading ? 'جارِ إنشاء الحساب...' : 'إنشاء الحساب'}
                {!loading && <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />}
              </button>

              <p className="text-center text-xs text-slate-500">
                لديك حساب مسبقاً؟{' '}
                <button type="button" onClick={() => navigate('/login')} className="font-semibold text-teal-400 hover:underline" data-testid="coach-register-login-link">
                  سجّل دخولك
                </button>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
